// Ported verbatim from system-web/components/icon.js (May 2026).
// Source of truth. New icon names should be added to BOTH files when reasonable.
import * as React from 'react';

import { brand } from '@brand';
import { renderBrandMark } from './brandMarks';

type IconName =
  | 'Logo'
  | 'Mark'
  | 'Mark Line'
  | 'Stars'
  | 'Apple'
  | 'Facebook'
  | 'Instagram'
  | 'Soundcloud'
  | 'Spotify'
  | 'Twitter'
  | 'YouTube'
  | 'Github'
  | 'Arrow'
  | 'Arrow Flat'
  | 'Arrow Down'
  | 'Arrow Tip'
  | 'Plus'
  | 'Minus'
  | 'Checkmark'
  | 'Chevron Down'
  | 'Close'
  | 'Expand'
  | 'Audio Off'
  | 'Audio On'
  | 'Play Video'
  | 'Pause Video'
  | 'Muted'
  | 'Unmuted'
  | 'Email';

interface IconProps {
  id?: string;
  name: IconName;
  color?: string;
  viewBox?: string;
  className?: string;
  /** The icon's accessible name, for an icon that is the only content of
   *  a control. Without one, and with `brand.iconTitles` off, the icon is
   *  decorative. */
  label?: string;
}

const getIcon = (name: IconName, color?: string) => {
  switch (name) {
    case 'Logo':
    case 'Mark':
    case 'Mark Line':
      return renderBrandMark(name);
    case 'Arrow':
      return (
        <path d="M9.75 5.62891L4.23492e-07 11.2581L9.15609e-07 -0.000259349L9.75 5.62891Z" fill="currentColor" />
      );
    case 'Arrow Flat':
      return (
        <path d="M16.1182 3.30859L9.5 6.61719V3.80859H0V2.80859H9.5V0L16.1182 3.30859Z" fill="#656565" />
      );
    case 'Arrow Down':
      return (
        <path d="M9 0V23M9 23L1 15M9 23L17 15" strokeWidth={2} stroke="currentColor" />
      );
    case 'Arrow Tip':
      return (
        <path d="M0.666504 1L3.99977 4.33327L0.666504 7.66653" fill="none" stroke="currentColor" />
      );
    case 'Plus':
      return (
        <>
          <path d="M5 0.5V10.5" stroke="currentColor" />
          <path d="M10 5.5L-4.76837e-07 5.5" stroke="currentColor" />
        </>
      );
    case 'Minus':
      return <rect x="20" y="46" width="60" height="8" fill={color ?? 'currentColor'} />;
    case 'Checkmark':
      return (
        <path
          fill="none"
          stroke={color ?? 'currentColor'}
          strokeWidth="13"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          d="M12.1 52.1l24.4 24.4 53-53"
        />
      );
    case 'Chevron Down':
      return (
        <polygon
          points="51.5 85.657 8.672 42.828 14.328 37.172 51.5 74.343 88.672 37.172 94.328 42.828 51.5 85.657"
          fill={color ?? 'currentColor'}
        />
      );
    case 'Close':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.9212 12.3352L20.293 21.7068L21.7072 20.2926L12.3355 10.9211L21.3759 1.88187L19.9617 0.467571L10.9213 9.50691L1.70717 0.292969L0.292969 1.7072L9.50699 10.921L0.293394 20.1335L1.70752 21.5478L10.9212 12.3352Z"
          fill="currentColor"
        />
      );
    case 'Email':
      return (
        <>
          <rect x="0.5" y="0.5" width="23" height="17" fill="none" stroke="currentColor" />
          <path d="M22.7452 0.5L12 10.6797L1.25479 0.5H22.7452Z" stroke="currentColor" fill="none" />
          <path d="M23 17.0517L14 8.52539" fill="none" stroke="currentColor" />
          <path d="M1 17.0517L10 8.52539" fill="none" stroke="currentColor" />
        </>
      );
    default:
      return <path />;
  }
};

/**
 * ArrowInline — shorthand for the canonical right-pointing CTA arrow.
 * Use anywhere copy used to end with `→` ("Continue intake", "View all",
 * "Purchase", etc.). Sized to sit inline with body text. Override via
 * `className` for larger filled-button slots.
 */
export const ArrowInline: React.FC<{ className?: string }> = ({ className }) => (
  <span
    aria-hidden
    className={`inline-flex items-center justify-center w-8 align-middle ${className ?? ''}`}
  >
    <Icon name="Arrow" viewBox="0 0 10 12" />
  </span>
);

export const Icon: React.FC<IconProps> = ({
  id = '',
  name,
  color,
  viewBox = '0 0 100 100',
  className,
  label,
}) => {
  if (!brand.iconTitles && !label) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={viewBox}
        width="100%"
        height="100%"
        aria-hidden="true"
        focusable="false"
        className={className ?? 'icon'}
      >
        {getIcon(name, color)}
      </svg>
    );
  }
  const titleId = `${name.replace(/\s/g, '').toLowerCase()}-${id}`;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width="100%"
      height="100%"
      aria-labelledby={titleId}
      className={className ?? 'icon'}
    >
      <title id={titleId}>{label ?? name}</title>
      {getIcon(name, color)}
    </svg>
  );
};

export default Icon;
